import { useCallback, useEffect, useRef, useState, type RefObject } from 'react'
import type { Editor } from '@tiptap/core'
import type { ComplianceDocumentSectionRecord } from '@artem-lex/compliance-documents'
import {
  areOutstandingCalloutRangesEqual,
  collectOutstandingCalloutRanges,
  type OutstandingCalloutRange,
} from './collectOutstandingCalloutRanges'
import { scrollToOutstandingCallout } from './scrollToOutstandingCallout'
import type { SectionEditorRegistration } from './ComplianceDocumentSectionBlock'

type UseOutstandingCalloutNavigatorParams<T extends ComplianceDocumentSectionRecord> =
  {
    enabled: boolean
    sections: T[]
    editorRegistrationsRef: RefObject<Map<string, SectionEditorRegistration>>
    scrollContainerRef: RefObject<HTMLElement | null>
    shouldIncludeSection: (section: T) => boolean
    editorRegistrationVersion: number
    contentSyncKey: number
  }

export function useOutstandingCalloutNavigator<
  T extends ComplianceDocumentSectionRecord,
>({
  enabled,
  sections,
  editorRegistrationsRef,
  scrollContainerRef,
  shouldIncludeSection,
  editorRegistrationVersion,
  contentSyncKey,
}: UseOutstandingCalloutNavigatorParams<T>) {
  const [hits, setHits] = useState<OutstandingCalloutRange[]>([])
  const [cursorIndex, setCursorIndex] = useState<number | null>(null)
  const rescanFrameRef = useRef<number | null>(null)
  const hitsRef = useRef<OutstandingCalloutRange[]>([])

  const getEditorForSection = useCallback(
    (sectionId: string): Editor | null => {
      const editor =
        editorRegistrationsRef.current.get(sectionId)?.editorRef.current ??
        null
      if (editor == null || editor.isDestroyed) {
        return null
      }
      return editor
    },
    [editorRegistrationsRef]
  )

  const rescanHits = useCallback(() => {
    if (!enabled) {
      return
    }

    const nextHits = collectOutstandingCalloutRanges({
      sections,
      getEditorForSection,
      shouldIncludeSection,
    })

    if (areOutstandingCalloutRangesEqual(hitsRef.current, nextHits)) {
      return
    }

    hitsRef.current = nextHits
    setHits(nextHits)
    setCursorIndex((currentIndex) => {
      if (nextHits.length === 0) {
        return null
      }
      if (currentIndex == null) {
        return null
      }
      if (currentIndex >= nextHits.length) {
        return nextHits.length - 1
      }
      return currentIndex
    })
  }, [enabled, getEditorForSection, sections, shouldIncludeSection])

  const scheduleRescanHits = useCallback(() => {
    if (rescanFrameRef.current != null) {
      return
    }
    rescanFrameRef.current = requestAnimationFrame(() => {
      rescanFrameRef.current = null
      rescanHits()
    })
  }, [rescanHits])

  useEffect(() => {
    if (!enabled) {
      hitsRef.current = []
      setHits([])
      setCursorIndex(null)
      return
    }

    rescanHits()
    let cancelled = false
    queueMicrotask(() => {
      if (!cancelled) {
        rescanHits()
      }
    })

    return () => {
      cancelled = true
    }
  }, [
    enabled,
    rescanHits,
    editorRegistrationVersion,
    contentSyncKey,
    sections,
  ])

  useEffect(() => {
    if (!enabled) {
      return
    }

    const subscriptions: Array<{ editor: Editor; handler: () => void }> = []

    for (const section of sections) {
      if (!shouldIncludeSection(section)) {
        continue
      }

      const editor = getEditorForSection(section.sectionId)
      if (editor == null) {
        continue
      }

      const handler = () => {
        scheduleRescanHits()
      }
      editor.on('update', handler)
      subscriptions.push({ editor, handler })
    }

    return () => {
      for (const { editor, handler } of subscriptions) {
        editor.off('update', handler)
      }
      if (rescanFrameRef.current != null) {
        cancelAnimationFrame(rescanFrameRef.current)
        rescanFrameRef.current = null
      }
    }
  }, [
    enabled,
    sections,
    getEditorForSection,
    shouldIncludeSection,
    scheduleRescanHits,
    editorRegistrationVersion,
    contentSyncKey,
  ])

  useEffect(() => {
    return () => {
      if (rescanFrameRef.current != null) {
        cancelAnimationFrame(rescanFrameRef.current)
      }
    }
  }, [])

  const jumpToHit = useCallback(
    (hit: OutstandingCalloutRange) => {
      const editor = getEditorForSection(hit.sectionId)
      const scrollContainer = scrollContainerRef.current
      if (editor == null || scrollContainer == null) {
        return
      }

      scrollToOutstandingCallout({
        hit,
        editor,
        scrollContainer,
      })
    },
    [getEditorForSection, scrollContainerRef]
  )

  const navigate = useCallback(
    (direction: 'next' | 'previous') => {
      if (hits.length === 0) {
        return
      }

      let nextIndex: number
      if (cursorIndex == null) {
        nextIndex = direction === 'next' ? 0 : hits.length - 1
      } else if (direction === 'next') {
        nextIndex = (cursorIndex + 1) % hits.length
      } else {
        nextIndex = (cursorIndex - 1 + hits.length) % hits.length
      }

      setCursorIndex(nextIndex)
      const hit = hits[nextIndex]
      if (hit != null) {
        jumpToHit(hit)
      }
    },
    [cursorIndex, hits, jumpToHit]
  )

  const handlePrevious = useCallback(() => {
    navigate('previous')
  }, [navigate])

  const handleNext = useCallback(() => {
    navigate('next')
  }, [navigate])

  return {
    count: hits.length,
    handlePrevious,
    handleNext,
  }
}
